<script setup lang="ts">
/**
 * A branded chat trigger button that replaces the old floating text input.
 * Combines an interactive hover effect (dot → slide reveal) with a gradient
 * border animation for visual polish.
 *
 * Two placement modes:
 *  - `header`  — inline in the admin toolbar, no fixed positioning.
 *  - `floating` — fixed to the bottom-right of the viewport (candidate).
 */
import { computed, ref } from "vue";

const props = withDefaults(
  defineProps<{
    /** Agent display name shown on the button. */
    agentName: string;
    /** Iconify slug for the agent icon (falls back to sparkle). */
    agentIcon?: string;
    /** Whether the button is disabled (e.g. chat not configured). */
    disabled?: boolean;
    /** Tooltip/reason shown when disabled. */
    disabledReason?: string;
    /** Controls positioning: `header` for inline, `floating` for fixed bottom-right. */
    placement?: "header" | "floating";
  }>(),
  {
    agentIcon: undefined,
    disabled: false,
    disabledReason: undefined,
    placement: "floating",
  },
);

const emit = defineEmits<{
  (e: "click"): void;
}>();

const buttonRef = ref<HTMLButtonElement>();

function focus() {
  buttonRef.value?.focus();
}
defineExpose({ focus });

const label = computed(() => `Chat with ${props.agentName}`);

const gradientColors = [
  "var(--primary)",
  "color-mix(in oklch, var(--primary) 70%, transparent)",
  "var(--chart-1)",
  "var(--chart-2)",
  "var(--chart-3)",
  "var(--primary)",
];
const allColors = gradientColors.join(", ");
</script>

<template>
  <div
    v-if="placement === 'floating'"
    class="pointer-events-none fixed inset-x-0 bottom-0 z-50 flex justify-center px-6 pb-[calc(env(safe-area-inset-bottom)+1.5rem)]"
    data-slot="chat-ai-button"
  >
    <div class="pointer-events-auto flex flex-col items-center gap-1.5">
      <button
        ref="buttonRef"
        :disabled="disabled"
        :title="disabled ? disabledReason : label"
        :aria-label="label"
        class="ai-btn ai-btn-enter animate-rainbow group relative flex cursor-pointer items-center justify-center overflow-hidden rounded-full shadow-lg transition-shadow hover:shadow-xl disabled:cursor-not-allowed disabled:opacity-50"
        @click="emit('click')"
      >
        <span class="btn-content btn-content-enter inline-flex items-center gap-2 overflow-hidden rounded-full bg-background px-5 py-2.5 text-sm font-semibold">
          <span class="relative flex items-center gap-2">
            <span class="icon-bloom shrink-0" aria-hidden="true">
              <Icon v-if="agentIcon" :name="agentIcon" mode="svg" class="relative z-10 size-5 text-primary transition-opacity duration-300 group-hover:opacity-0" />
              <Icon v-else name="lucide:sparkles" mode="svg" class="relative z-10 size-5 text-primary transition-opacity duration-300 group-hover:opacity-0" />
            </span>
            <span class="whitespace-nowrap transition-all duration-300 group-hover:translate-x-10 group-hover:opacity-0">
              {{ label }}
            </span>
          </span>

          <span
            class="absolute right-3 z-10 flex translate-x-10 items-center gap-1.5 text-primary-foreground opacity-0 transition-all duration-300 group-hover:translate-x-0 group-hover:opacity-100"
          >
            <span class="whitespace-nowrap">{{ label }}</span>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="lucide lucide-arrow-right"
            >
              <path d="M5 12h14" />
              <path d="m12 5 7 7-7 7" />
            </svg>
          </span>
        </span>
      </button>

      <p
        v-if="disabled && disabledReason"
        class="px-1 text-center text-xs text-muted-foreground"
        role="status"
      >
        {{ disabledReason }}
      </p>
    </div>
  </div>

  <!-- Header-inline variant (admin) -->
  <button
    v-else
    ref="buttonRef"
    :disabled="disabled"
    :title="disabled ? disabledReason : label"
    :aria-label="label"
    class="ai-btn ai-btn-enter animate-rainbow group relative flex cursor-pointer items-center justify-center overflow-hidden rounded-full transition-shadow hover:shadow-md disabled:cursor-not-allowed disabled:opacity-50"
    data-slot="chat-ai-button"
    @click="emit('click')"
  >
    <span class="btn-content btn-content-enter inline-flex items-center gap-2 overflow-hidden rounded-full bg-background px-4 py-1.5 text-sm font-semibold">
      <span class="relative flex items-center gap-2">
        <span class="icon-bloom icon-bloom-sm shrink-0" aria-hidden="true">
          <Icon v-if="agentIcon" :name="agentIcon" mode="svg" class="relative z-10 size-4 text-primary transition-opacity duration-300 group-hover:opacity-0" />
          <Icon v-else name="lucide:sparkles" mode="svg" class="relative z-10 size-4 text-primary transition-opacity duration-300 group-hover:opacity-0" />
        </span>
        <span class="whitespace-nowrap transition-all duration-300 group-hover:translate-x-10 group-hover:opacity-0">
          {{ label }}
        </span>
      </span>

      <span
        class="absolute right-3 z-10 flex translate-x-10 items-center gap-1.5 text-primary-foreground opacity-0 transition-all duration-300 group-hover:translate-x-0 group-hover:opacity-100"
      >
        <span class="whitespace-nowrap">{{ label }}</span>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          class="lucide lucide-arrow-right"
        >
          <path d="M5 12h14" />
          <path d="m12 5 7 7-7 7" />
        </svg>
      </span>
    </span>
  </button>
</template>

<style scoped>
.ai-btn {
  padding: 2px;
  border-radius: 9999px;
}

.ai-btn::before {
  content: "";
  position: absolute;
  inset: -200%;
  background: conic-gradient(v-bind(allColors));
  animation: rotate-rainbow 2500ms linear infinite;
  filter: blur(4px);
  padding: 2px;
}

.btn-content {
  z-index: 1;
}

.icon-bloom {
  --bloom-size: 1.25rem;

  position: relative;
  isolation: isolate;
}

.icon-bloom-sm {
  --bloom-size: 1rem;
}

.icon-bloom::before {
  content: "";
  position: absolute;
  left: 50%;
  top: 50%;
  width: var(--bloom-size);
  height: var(--bloom-size);
  border-radius: 9999px;
  background: var(--primary);
  transform: translate(-50%, -50%) scale(0);
  transform-origin: center;
  transition: transform 300ms ease;
  z-index: -1;
}

.ai-btn:not(:disabled):hover .icon-bloom::before,
.ai-btn:not(:disabled):focus-visible .icon-bloom::before {
  transform: translate(-50%, -50%) scale(80);
}

@keyframes rotate-rainbow {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.ai-btn-enter {
  transform-origin: center;
  animation: ai-btn-materialize 720ms cubic-bezier(0.16, 1, 0.3, 1) both;
}

.btn-content-enter {
  animation: ai-btn-content-reveal 520ms ease-out 260ms both;
}

.ai-btn-enter::after {
  content: "";
  position: absolute;
  inset: 2px;
  border-radius: 9999px;
  background: var(--primary);
  transform: scale(0);
  opacity: 0;
  z-index: 2;
  pointer-events: none;
  animation: ai-btn-flash-core 680ms cubic-bezier(0.16, 1, 0.3, 1) both;
}

@keyframes ai-btn-materialize {
  0% {
    opacity: 0;
    transform: scale(0.08);
    clip-path: circle(0.45rem at 50% 50%);
    filter: blur(1px);
  }

  28% {
    opacity: 1;
    transform: scale(0.16);
    clip-path: circle(0.55rem at 50% 50%);
    filter: blur(0);
  }

  62% {
    transform: scale(1.06);
    clip-path: circle(140% at 50% 50%);
  }

  82% {
    transform: scale(0.98);
  }

  100% {
    opacity: 1;
    transform: scale(1);
    clip-path: circle(140% at 50% 50%);
    filter: blur(0);
  }
}

@keyframes ai-btn-content-reveal {
  0% {
    opacity: 0;
    transform: translateY(0.25rem) scale(0.96);
  }

  100% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes ai-btn-flash-core {
  0% {
    opacity: 1;
    transform: scale(0.05);
  }

  42% {
    opacity: 1;
    transform: scale(1);
  }

  100% {
    opacity: 0;
    transform: scale(1.18);
  }
}

@media (prefers-reduced-motion: reduce) {
  .ai-btn::before {
    animation: none;
  }

  .ai-btn-enter,
  .btn-content-enter,
  .ai-btn-enter::after {
    animation: none;
  }
}
</style>
